<template>
  <div class="list-page-container">
    <div class="control-panel">
      <div class="control-container">
        <div class="header-info">
          <i class="el-icon-chat-dot-round"></i>
          <span>管理社区活动信息，发布活动通知，跟踪活动状态</span>
        </div>
        <div class="action-wrapper">
          <el-button
            v-if="hasPermission('activity:community:save')"
            type="primary"
            icon="el-icon-plus"
            @click="handleAdd"
          >
            发布活动
          </el-button>
        </div>
      </div>
    </div>

    <div class="control-panel has-query">
      <div class="control-container">
        <el-form :inline="true" :model="searchForm" class="filter-form" @submit.native.prevent>
          <el-form-item>
            <el-input v-model="searchForm.title" placeholder="活动标题" clearable class="filter-input" @keyup.enter.native="handleSearch" @clear="handleSearch">
              <i slot="prefix" class="el-input__icon el-icon-search"></i>
            </el-input>
          </el-form-item>
          <el-form-item>
            <el-select v-model="searchForm.category" placeholder="活动分类" clearable class="filter-select" @change="handleSearch">
              <el-option v-for="item in categoryOptions" :key="item.key" :label="item.name" :value="item.key"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-select v-model="searchForm.status" placeholder="活动状态" clearable class="filter-select" @change="handleSearch">
              <el-option label="草稿" value="DRAFT"></el-option>
              <el-option label="已发布" value="PUBLISHED"></el-option>
              <el-option label="已归档" value="ARCHIVED"></el-option>
              <el-option label="已取消" value="CANCELED"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-date-picker v-model="searchForm.dateRange" type="daterange" range-separator="至" start-placeholder="活动开始" end-placeholder="活动结束" value-format="yyyy-MM-dd" class="filter-date" @change="handleSearch"></el-date-picker>
          </el-form-item>
          <div class="search-btns">
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">搜索</el-button>
            <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
          </div>
        </el-form>
      </div>
    </div>

    <div class="statistics-panel" v-if="statistics">
      <div class="stat-card stat-total">
        <div class="stat-value">{{ statistics.total || 0 }}</div>
        <div class="stat-label">全部活动</div>
      </div>
      <div class="stat-card stat-draft">
        <div class="stat-value">{{ statistics.draft || 0 }}</div>
        <div class="stat-label">草稿</div>
      </div>
      <div class="stat-card stat-published">
        <div class="stat-value">{{ statistics.published || 0 }}</div>
        <div class="stat-label">已发布</div>
      </div>
      <div class="stat-card stat-archived">
        <div class="stat-value">{{ statistics.archived || 0 }}</div>
        <div class="stat-label">已归档</div>
      </div>
      <div class="stat-card stat-canceled">
        <div class="stat-value">{{ statistics.canceled || 0 }}</div>
        <div class="stat-label">已取消</div>
      </div>
    </div>

    <el-card class="list-card">
      <el-table ref="table" :data="tableData" v-loading="loading" class="custom-table" height="100%" style="width: 100%" @sort-change="handleSortChange">
        <el-table-column prop="id" label="ID" width="70" align="center" v-if="false"></el-table-column>
        <el-table-column label="封面" width="90" align="center">
          <template slot-scope="scope">
            <el-image v-if="scope.row.coverImage" :src="resolveUrl(scope.row.coverImage)" fit="cover" style="width: 60px; height: 40px; border-radius: 4px;"></el-image>
            <span v-else style="color: #c0c4cc;">-</span>
          </template>
        </el-table-column>
        <el-table-column prop="title" label="活动标题" min-width="200" show-overflow-tooltip></el-table-column>
        <el-table-column prop="category" label="分类" width="100" align="center">
          <template slot-scope="scope">
            <el-tag size="mini" effect="plain" :type="getCategoryTagType(scope.row.category)">{{ getCategoryLabel(scope.row.category) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="90" align="center">
          <template slot-scope="scope">
            <el-tag :type="getStatusType(scope.row.status)" size="mini" effect="plain">{{ getStatusLabel(scope.row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="isTop" label="置顶" width="70" align="center">
          <template slot-scope="scope">
            <el-switch
              :value="scope.row.isTop"
              :disabled="scope.row.status !== 'PUBLISHED'"
              size="mini"
              @change="handleTopChange(scope.row)"
            ></el-switch>
          </template>
        </el-table-column>
        <el-table-column label="活动时间" width="180" align="center">
          <template slot-scope="scope">
            <template v-if="scope.row.activityStartTime">
              <div style="font-size: 12px;">{{ formatDate(scope.row.activityStartTime) }}</div>
              <div style="font-size: 12px; color: #909399;">至 {{ formatDate(scope.row.activityEndTime) }}</div>
            </template>
            <span v-else style="color: #c0c4cc;">-</span>
          </template>
        </el-table-column>
        <el-table-column prop="activityLocation" label="地点" width="120" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.activityLocation || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="参与" width="80" align="center">
          <template slot-scope="scope">
            <span>{{ scope.row.currentParticipants || 0 }}<template v-if="scope.row.maxParticipants">/{{ scope.row.maxParticipants }}</template></span>
          </template>
        </el-table-column>
        <el-table-column prop="viewCount" label="浏览" width="70" align="center">
          <template slot-scope="scope">
            {{ scope.row.viewCount || 0 }}
          </template>
        </el-table-column>
        <el-table-column v-if="hasAnyActionPermission" :label="$t('common.operation')" :width="actionColumnWidth" fixed="right" align="center">
          <template slot-scope="scope">
            <div class="action-buttons">
              <el-button v-if="hasPermission('activity:community:info')" type="primary" size="mini" plain icon="el-icon-view" @click="handleView(scope.row)">{{ $t('common.detail') }}</el-button>
              <el-button v-if="hasPermission('activity:community:update')" type="warning" size="mini" plain icon="el-icon-edit" @click="handleEdit(scope.row)">{{ $t('common.edit') }}</el-button>
              <el-button v-if="hasPermission('activity:community:update')" class="btn-action-status" size="mini" plain icon="el-icon-setting" @click="openStatusDialog(scope.row)">{{ $t('common.status') }}</el-button>
              <el-button v-if="hasPermission('activity:community:delete')" type="danger" size="mini" plain icon="el-icon-delete" @click="handleDelete(scope.row)">{{ $t('common.delete') }}</el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="limit"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
        ></el-pagination>
      </div>
    </el-card>

    <el-dialog title="变更活动状态" :visible.sync="statusDialogVisible" width="500px" :close-on-click-modal="false">
      <div v-if="statusChangeRow" style="margin-bottom: 16px;">
        <span>当前活动：</span><strong>{{ statusChangeRow.title }}</strong>
        <el-tag :type="getStatusType(statusChangeRow.status)" size="small" style="margin-left: 8px;">{{ getStatusLabel(statusChangeRow.status) }}</el-tag>
      </div>
      <el-form ref="statusForm" :model="statusFormData" :rules="statusFormRules" label-width="80px">
        <el-form-item label="目标状态" prop="targetStatus">
          <el-select v-model="statusFormData.targetStatus" placeholder="请选择目标状态" style="width: 100%">
            <el-option label="草稿" value="DRAFT"></el-option>
            <el-option label="已发布" value="PUBLISHED"></el-option>
            <el-option label="已归档" value="ARCHIVED"></el-option>
            <el-option label="已取消" value="CANCELED"></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="statusDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleStatusChange" :loading="statusLoading">确认变更</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getActivityPage,
  getActivityStatistics,
  deleteActivity,
  updateActivityStatus,
  setActivityTop
} from '@/apis/activity/activity'
import { getDictDataByType } from '@/apis/system/dict'
import { hasPermission } from '@/utils/permission'
import { getFullUrl } from '@/utils'

export default {
  name: 'CommunityList',
  data() {
    return {
      loading: false,
      tableData: [],
      page: 1,
      limit: 10,
      total: 0,
      categoryOptions: [],
      categoryMap: {},
      searchForm: {
        title: '',
        category: '',
        status: '',
        dateRange: null
      },
      statistics: null,
      statusDialogVisible: false,
      statusChangeRow: null,
      statusFormData: {
        targetStatus: ''
      },
      statusFormRules: {
        targetStatus: [{ required: true, message: '请选择目标状态', trigger: 'change' }]
      },
      statusLoading: false
    }
  },
  created() {
    this.loadCategoryDict()
    this.loadData()
    this.loadStatistics()
  },
  computed: {
    visibleActionCount() {
      let count = 0
      if (this.hasPermission('activity:community:info')) count += 1
      if (this.hasPermission('activity:community:update')) count += 2
      if (this.hasPermission('activity:community:delete')) count += 1
      return count
    },
    hasAnyActionPermission() {
      return this.visibleActionCount > 0
    },
    actionColumnWidth() {
      const count = this.visibleActionCount
      const widthMap = { 1: 100, 2: 170, 3: 245, 4: 300 }
      return widthMap[count] || 0
    }
  },
  methods: {
    hasPermission,
    resolveUrl(url) {
      return getFullUrl(url)
    },
    loadCategoryDict() {
      getDictDataByType('ACTIVITY_CATEGORY').then(res => {
        const data = res || []
        this.categoryOptions = data
        this.categoryMap = {}
        data.forEach(item => {
          this.categoryMap[item.key] = item.name
        })
      }).catch(e => {
        console.error('加载活动分类字典失败:', e)
        this.categoryOptions = []
      })
    },
    loadData() {
      this.loading = true
      const params = {
        page: this.page,
        limit: this.limit,
        title: this.searchForm.title || undefined,
        category: this.searchForm.category || undefined,
        status: this.searchForm.status || undefined,
        startDate: this.searchForm.dateRange ? this.searchForm.dateRange[0] : undefined,
        endDate: this.searchForm.dateRange ? this.searchForm.dateRange[1] : undefined
      }
      getActivityPage(params).then(res => {
        if (res) {
          this.tableData = res.list || []
          this.total = res.total || 0
        }
      }).catch(e => {
        console.error('加载活动列表失败:', e)
      }).finally(() => {
        this.loading = false
      })
    },
    loadStatistics() {
      getActivityStatistics({}).then(res => {
        if (res) {
          this.statistics = res
        }
      }).catch(e => {
        console.error('加载活动统计失败:', e)
      })
    },
    handleSearch() {
      this.page = 1
      this.loadData()
    },
    handleReset() {
      this.searchForm = { title: '', category: '', status: '', dateRange: null }
      this.page = 1
      this.loadData()
    },
    handleSizeChange(val) {
      this.limit = val
      this.page = 1
      this.loadData()
    },
    handleCurrentChange(val) {
      this.page = val
      this.loadData()
    },
    handleSortChange({ prop, order }) {
      this.loadData()
    },
    handleAdd() {
      this.$router.push('/activity/community/add')
    },
    handleView(row) {
      this.$router.push(`/activity/community/detail/${row.id}`)
    },
    handleEdit(row) {
      this.$router.push(`/activity/community/edit/${row.id}`)
    },
    handleDelete(row) {
      this.$confirm('确定要删除该活动吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        deleteActivity([row.id]).then(() => {
          this.$message.success('删除成功')
          this.loadData()
          this.loadStatistics()
        })
      }).catch(() => {})
    },
    openStatusDialog(row) {
      this.statusChangeRow = row
      this.statusFormData.targetStatus = ''
      this.statusDialogVisible = true
    },
    handleTopChange(row) {
      const newTop = !row.isTop
      const actionText = newTop ? '置顶' : '取消置顶'
      this.$confirm(`确定要${actionText}该活动吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        setActivityTop(row.id, newTop).then(() => {
          this.$message.success(`${actionText}成功`)
          this.loadData()
          this.loadStatistics()
        })
      }).catch(() => {})
    },
    handleStatusChange() {
      this.$refs.statusForm.validate(valid => {
        if (!valid) return
        this.statusLoading = true
        updateActivityStatus(this.statusChangeRow.id, this.statusFormData.targetStatus).then(() => {
          this.$message.success('状态变更成功')
          this.statusDialogVisible = false
          this.loadData()
          this.loadStatistics()
        }).finally(() => {
          this.statusLoading = false
        })
      })
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    },
    getCategoryLabel(category) {
      return this.categoryMap[category] || category || '-'
    },
    getCategoryTagType(category) {
      const map = { ONLINE: '', OFFLINE: 'success', PROMOTION: 'warning', COMMUNITY: 'info', OTHER: 'info' }
      return map[category] || 'info'
    },
    getStatusLabel(status) {
      const map = { DRAFT: '草稿', PUBLISHED: '已发布', ARCHIVED: '已归档', CANCELED: '已取消' }
      return map[status] || status
    },
    getStatusType(status) {
      const map = { DRAFT: 'info', PUBLISHED: 'success', ARCHIVED: 'warning', CANCELED: 'danger' }
      return map[status] || 'info'
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/styles/list-page.scss";

.statistics-panel {
  display: flex;
  gap: 12px;
  margin-top: 10px;
  flex-wrap: wrap;
}

.stat-card {
  flex: 1;
  min-width: 120px;
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  text-align: center;
  border: 1px solid #f1f5f9;
  border-left: 3px solid #409eff;
  transition: box-shadow 0.3s;

  &:hover {
    box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.06);
  }

  &.stat-draft { border-left-color: #909399; }
  &.stat-published { border-left-color: #67c23a; }
  &.stat-archived { border-left-color: #e6a23c; }
  &.stat-canceled { border-left-color: #f56c6c; }
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #303133;
}

.stat-label {
  font-size: 13px;
  color: #909399;
  margin-top: 4px;
}

.filter-date {
  width: 260px;
}
</style>
